
import { useState, useCallback, useRef, useEffect } from "react";
import { countFilteredPrompts, countTotalPrompts } from "@/utils/exploreQueries";

interface UseCategoryFilterProps {
  initialCategoryId?: string | null;
  onCategoryChange?: (categoryId: string | null) => void;
}

export function useCategoryFilter({ 
  initialCategoryId = null,
  onCategoryChange 
}: UseCategoryFilterProps = {}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategoryId);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const isLoadingCount = useRef(false);
  const initialCountLoaded = useRef(false);

  console.log("🔍 useCategoryFilter - State:", { 
    initialCategoryId, 
    selectedCategory, 
    totalItems, 
    initialCountLoaded: initialCountLoaded.current 
  });

  // CRITICAL FIX: Force update selectedCategory when initialCategoryId changes
  useEffect(() => {
    console.log(`🔄 useCategoryFilter: Checking if update needed - initialCategoryId: ${initialCategoryId}, selectedCategory: ${selectedCategory}`);
    
    // Always update if different, including null values
    if (initialCategoryId !== selectedCategory) {
      console.log(`🎯 useCategoryFilter: FORCING category update from ${selectedCategory} to ${initialCategoryId}`);
      setSelectedCategory(initialCategoryId);
      
      // Reset count loading flag to allow new count to be loaded
      initialCountLoaded.current = false;
      
      // Trigger count loading for the new category
      if (initialCategoryId && !isLoadingCount.current) {
        loadCountForCategory(initialCategoryId);
      } else if (!initialCategoryId && !isLoadingCount.current) {
        loadTotalCount();
      }
    }
  }, [initialCategoryId]);

  // Helper function to load count for a specific category
  const loadCountForCategory = async (categoryId: string) => {
    if (isLoadingCount.current) return;
    
    console.log(`📊 useCategoryFilter: Loading count for category: ${categoryId}`);
    isLoadingCount.current = true;
    
    try {
      const count = await countFilteredPrompts(categoryId);
      console.log(`✅ useCategoryFilter: Category count loaded: ${count} for category: ${categoryId}`);
      setTotalItems(count);
      initialCountLoaded.current = true;
    } catch (error) {
      console.error("❌ useCategoryFilter: Error loading category count:", error);
    } finally {
      isLoadingCount.current = false;
    }
  };

  // Helper function to load total count
  const loadTotalCount = async () => {
    if (isLoadingCount.current) return;
    
    console.log("📊 useCategoryFilter: Loading total count for all prompts");
    isLoadingCount.current = true;
    
    try {
      const count = await countTotalPrompts();
      console.log(`✅ useCategoryFilter: Total count loaded: ${count}`);
      setTotalItems(count);
      initialCountLoaded.current = true;
    } catch (error) {
      console.error("❌ useCategoryFilter: Error loading total count:", error);
    } finally {
      isLoadingCount.current = false;
    }
  };

  // Load initial count when component mounts
  useEffect(() => {
    const loadInitialCount = async () => {
      if (initialCountLoaded.current || isLoadingCount.current) {
        console.log("⏭️ useCategoryFilter: Count already loaded or loading, skipping initial load");
        return;
      }
      
      if (selectedCategory) {
        await loadCountForCategory(selectedCategory);
      } else {
        await loadTotalCount();
      }
    };
    
    loadInitialCount();
  }, [selectedCategory]);

  const handleCategoryFilter = useCallback(async (categoryId: string) => {
    console.log(`🎯 useCategoryFilter: Category filter requested: ${categoryId}`);
    
    // Handle unselect - empty string means unselect
    const newCategoryId = categoryId === '' ? null : categoryId;
    
    // Don't do anything if the category is already selected
    if (selectedCategory === newCategoryId) {
      console.log("⏭️ useCategoryFilter: Category already selected, no change needed");
      return;
    }
    
    setSelectedCategory(newCategoryId);
    console.log(`🔄 useCategoryFilter: Selected category changed to: ${newCategoryId}`);
    
    // Notify parent component of category change
    if (onCategoryChange) {
      onCategoryChange(newCategoryId);
    }
    
    // Load count for new category
    if (newCategoryId) {
      await loadCountForCategory(newCategoryId);
    } else {
      await loadTotalCount();
    }
  }, [selectedCategory, onCategoryChange]);

  const clearCategoryFilter = useCallback(() => {
    console.log("🧹 useCategoryFilter: Clearing category filter");
    handleCategoryFilter(''); // Use empty string to trigger unselect
  }, [handleCategoryFilter]);

  // Debug final state
  useEffect(() => {
    console.log('📊 useCategoryFilter: Final state update:', {
      selectedCategory,
      totalItems,
      initialCategoryId
    });
  }, [selectedCategory, totalItems, initialCategoryId]);

  return {
    selectedCategory,
    handleCategoryFilter,
    clearCategoryFilter,
    setSelectedCategory,
    totalItems
  };
}
